import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "./ThemeContext";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { lazy, Suspense, useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import Footer from "./components/Footer";
import Banner from "./components/Banner";
import { connectSocket, disconnectSocket } from "./socket";
import PageTitle from "./components/PageTitle";
import PageTransition from "./components/PageTransition";
import { usePageTransition } from "./hooks/usePageTransition";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

if (typeof window !== "undefined") {
  window.history.scrollRestoration = "manual";
}

const Landing = lazy(() => import("./pages/Landing"));
const Home = lazy(() => import("./pages/Home"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/Authcallback.jsx"));
const ItemDetail = lazy(() => import("./pages/ItemDetail"));
const PostItem = lazy(() => import("./pages/PostItem"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Messages = lazy(() => import("./pages/Messages"));
const Orders = lazy(() => import("./pages/Orders"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Settings = lazy(() => import("./pages/Settings"));
const SellerSetup = lazy(() => import("./pages/SellerSetup"));
const FindSellers = lazy(() => import("./pages/FindSellers"));
const SellerProfile = lazy(() => import("./pages/SellerProfile"));
const Watching = lazy(() => import("./pages/Watching"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ServiceDown = lazy(() => import("./pages/ServiceDown"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

function ProfileNudge() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("profileNudgeDismissed") === "1");
  const [completing, setCompleting] = useState(false);
  const [prevPath, setPrevPath] = useState(location.pathname);
  if (prevPath !== location.pathname) {
    setPrevPath(location.pathname);
    setCompleting(false);
  }
  if (!user || user.institution || location.pathname === "/" || location.pathname === "/settings" || location.pathname === "/checkout" || location.pathname === "/seller/setup" || location.pathname === "/terms" || location.pathname === "/privacy" || dismissed) return null;
  const dismiss = () => { sessionStorage.setItem("profileNudgeDismissed", "1"); setDismissed(true); };
  return (
      <div className="bg-accent-soft border-b border-border">
      <div className="max-w-3xl mx-auto pl-10 pr-4 md:pl-16 md:pr-8 py-2.5 flex items-center gap-2.5 md:gap-3">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-accent flex-shrink-0 hidden sm:block">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
        <span className="text-xs sm:text-sm text-text-secondary min-w-0">Complete your profile to unlock all features.</span>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto shrink-0">
          <button onClick={() => { setCompleting(true); setTimeout(() => navigate("/settings?section=institution&focus=institution"), 500); }}
            disabled={completing}
            className="px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold text-white bg-accent rounded-lg hover:bg-accent-hover transition-all flex-shrink-0 flex items-center justify-center disabled:opacity-60 active:scale-[0.98]">
            {completing ? (
              <div className="w-3 h-3 border-[1.5px] border-white/30 border-t-white rounded-full animate-spin" />
            ) : "Complete"}
          </button>
          <button onClick={dismiss} className="w-6 h-6 flex items-center justify-center rounded-md text-text-muted bg-black/5 dark:bg-white/10 hover:text-text hover:bg-black/10 dark:hover:bg-white/15 transition-colors flex-shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceOverlay() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener("service-down", handler);
    return () => window.removeEventListener("service-down", handler);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-bg/80 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-2xl shadow-2xl p-8 max-w-sm mx-5 text-center">
        <img src="/assets/503-error.svg" alt="" className="w-48 h-48 object-contain mx-auto mb-4" />
        <h2 className="text-xl font-medium text-text mb-1 font-brand">Service unavailable.</h2>
        <p className="text-sm text-text-secondary mb-5">Something went wrong. Please try again.</p>
        <button onClick={() => { setShow(false); window.location.reload(); }} className="px-5 py-2.5 bg-accent text-white text-sm font-semibold rounded-xl hover:bg-accent-hover transition-colors">
          Try Again
        </button>
      </div>
    </div>
  );
}

function AppInner() {
  const location = useLocation();
  const { open } = useSidebar();
  const { displayedLocation } = usePageTransition(location);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  const isAdminPage = displayedLocation.pathname.startsWith("/admin");
  const hideNav = displayedLocation.pathname === "/" || displayedLocation.pathname === "/login" || displayedLocation.pathname === "/register" || displayedLocation.pathname === "/auth/callback" || displayedLocation.pathname === "/checkout" || displayedLocation.pathname === "/forgot-password" || displayedLocation.pathname === "/reset-password";

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const token = localStorage.getItem("token");
    if (user?.id && token) connectSocket(user.id);
    else disconnectSocket();
  }, [location.pathname]);

  const isHome = displayedLocation.pathname === "/browse";
  const isPost = displayedLocation.pathname === "/post";
  const isSellerProfile = displayedLocation.pathname.startsWith("/users/");
  const isFindSellers = displayedLocation.pathname === "/sellers";
  const isMessages = displayedLocation.pathname === "/messages";

  return (
    <div className="min-h-screen bg-bg text-text overflow-x-clip">
      {!isAdminPage && <PageTitle />}
      {!isAdminPage && !hideNav && <Sidebar />}
      {!isAdminPage && !hideNav && <Navbar />}
      <div className={`transition-[margin] duration-300 ease-out ${isAdminPage ? "" : hideNav ? "" : isHome ? "pt-28 sm:pt-24" : isPost ? "pt-16" : "pt-16 lg:pt-24 xl:pt-16"}`} style={{ marginLeft: isAdminPage || hideNav ? "0px" : isDesktop && open ? "180px" : "0px" }}>
        {!isAdminPage && <ProfileNudge />}
        <PageTransition pathname={displayedLocation.pathname}>
          {isAdminPage ? (
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-6 h-6 border-2 border-border border-t-accent rounded-full animate-spin" /></div>}>
              <Routes location={displayedLocation}>
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/users" element={<AdminPage />} />
                <Route path="/admin/items" element={<AdminPage />} />
                <Route path="/admin/transactions" element={<AdminPage />} />
                <Route path="/admin/verifications" element={<AdminPage />} />
                <Route path="/admin/reports" element={<AdminPage />} />
                <Route path="/admin/verified" element={<AdminPage />} />
                <Route path="/admin/search" element={<AdminPage />} />
                <Route path="/admin/activity" element={<AdminPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          ) : (
            <main>
              <Suspense fallback={<div className="flex items-center justify-center min-h-[calc(100vh-6rem)]"><div className="w-6 h-6 border-2 border-border border-t-accent rounded-full animate-spin" /></div>}>
                <Routes location={displayedLocation}>
                <Route path="/" element={<Landing />} />
                <Route path="/browse" element={<Home />} />
            <Route path="/home" element={<Navigate to="/browse" replace />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/register" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/items/:id" element={<ItemDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/post" element={<ProtectedRoute><PostItem /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/watching" element={<ProtectedRoute><Watching /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/seller/setup" element={<ProtectedRoute><SellerSetup /></ProtectedRoute>} />
            <Route path="/sellers" element={<FindSellers />} />
            <Route path="/users/:id" element={<SellerProfile />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
          )}
        </PageTransition>
        {!isAdminPage && !hideNav && <BottomNav />}
        {!isAdminPage && !hideNav && !isSellerProfile && !isFindSellers && !isMessages && !isPost && <Footer />}
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SidebarProvider>
          <Analytics />
          <AppInner />
          <ServiceOverlay />
          <SpeedInsights />
        </SidebarProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;